import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import styled from 'styled-components';
import {
    RebelCard,
    RebelButton,
    RebelSecondaryButton,
    RebelDangerButton,
    RebelLabel,
    RebelInput,
    RebelTextarea,
    RebelSelect,
    RebelIcon,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter
} from '../../../styles/rebel-experiences';
import { GetDB } from '../../../utils/database/firebase-budget-tool';
import { listenToDocument, listenToCollection, setDocument, deleteDocument } from '../../../utils/firestore-functions';
import AgendaActivityForm from './agenda-activity-form';

const AgendaLocationForm = ({ id }) => {
    const [activeTab, setActiveTab] = useState('details');
    const [formData, setFormData] = useState({
        location_name: '',
        country: '',
        currency: 'USD',
        timezone: '',
        description: '',
        location_image_url: '',
        is_active: true
    });
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isNewLocation, setIsNewLocation] = useState(true);
    const [editingActivity, setEditingActivity] = useState(null);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [deleteActivityModal, setDeleteActivityModal] = useState(false);
    const [activityToDelete, setActivityToDelete] = useState(null);
    const [activityFilters, setActivityFilters] = useState({
        searchTerm: '',
        category: '',
        minPrice: '',
        maxPrice: '',
        minDuration: '',
        maxDuration: ''
    });


    const budgetDB = GetDB();

    useEffect(() => {
        const unsubscribeLocation = listenToDocument(budgetDB, "agenda-config-locations", id, (data) => {
            if (data) {
                setFormData(data);
                setIsNewLocation(false);
            } else {
                setIsNewLocation(true);
            }
            setLoading(false);
        });

        const unsubscribeActivities = listenToCollection(budgetDB, "agenda-config-activities", (data) => {
            const locationActivities = (data || []).filter(act => act.location_id === id);
            setActivities(locationActivities);
        });

        return () => {
            unsubscribeLocation();
            unsubscribeActivities();
        };
    }, [id, budgetDB]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const dataToSave = {
                ...formData,
                id: id,
                updated_at: new Date()
            };

            if (isNewLocation) {
                dataToSave.created_at = new Date();
            }

            // Wait for the save to complete before redirecting
            await new Promise((resolve, reject) => {
                setDocument(
                    budgetDB,
                    "agenda-config-locations",
                    id,
                    dataToSave,
                    () => resolve(),  // success callback
                    (error) => reject(error)  // failure callback
                );
            });

            window.location = "/budget-app/agenda-config";
        } catch (error) {
            console.error('Error saving location:', error);
            alert('Error saving location. Please try again.');
            setSaving(false);
        }
    };

    const handleCancel = () => {
        window.location = "/budget-app/agenda-config";
    };

    const openActivityModal = (activity = null) => {
        setEditingActivity(activity);
        setShowActivityModal(true);
    };

    const closeActivityModal = () => {
        setShowActivityModal(false);
        setEditingActivity(null);
    };

    const confirmDeleteActivity = (activity) => {
        setActivityToDelete(activity);
        setDeleteActivityModal(true);
    };

    const handleDeleteActivity = async () => {
        if (activityToDelete) {
            await deleteDocument(budgetDB, "agenda-config-activities", activityToDelete.id);
            setDeleteActivityModal(false);
            setActivityToDelete(null);
        }
    };

    const handleFilterChange = (field, value) => {
        setActivityFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getFilteredActivities = () => {
        return activities.filter(activity => {
            // Search term filter (name, description)
            if (activityFilters.searchTerm) {
                const searchLower = activityFilters.searchTerm.toLowerCase();
                const matchesName = activity.activity_name?.toLowerCase().includes(searchLower);
                const matchesDescription = activity.description?.toLowerCase().includes(searchLower);
                const matchesSubCategory = activity.sub_category?.toLowerCase().includes(searchLower);
                if (!matchesName && !matchesDescription && !matchesSubCategory) return false;
            }

            // Category filter
            if (activityFilters.category && activity.category !== activityFilters.category) {
                return false;
            }

            // Price filters
            const totalPrice = (parseFloat(activity.price_per_person) || 0) + (parseFloat(activity.flat_price) || 0);
            if (activityFilters.minPrice && totalPrice < parseFloat(activityFilters.minPrice)) {
                return false;
            }
            if (activityFilters.maxPrice && totalPrice > parseFloat(activityFilters.maxPrice)) {
                return false;
            }

            // Duration filters
            const duration = parseFloat(activity.duration_hours) || 0;
            if (activityFilters.minDuration && duration < parseFloat(activityFilters.minDuration)) {
                return false;
            }
            if (activityFilters.maxDuration && duration > parseFloat(activityFilters.maxDuration)) {
                return false;
            }

            return true;
        });
    };

    const filteredActivities = getFilteredActivities();

    if (loading) {
        return (
            <RebelCard>
                <div style={{ textAlign: 'center', padding: '48px' }}>
                    <p style={{ color: '#b0b0b0' }}>Loading location...</p>
                </div>
            </RebelCard>
        );
    }

    return (
        <React.Fragment>
            <RebelCard>
                <TabContainer>
                    <TabGroup>
                        <TabButton
                            active={activeTab === 'details'}
                            onClick={() => setActiveTab('details')}
                        >
                            <RebelIcon className="fas fa-info-circle" style={{ marginRight: '8px' }} />
                            Location Details
                        </TabButton>
                        <TabButton
                            active={activeTab === 'activities'}
                            onClick={() => setActiveTab('activities')}
                            disabled={isNewLocation}
                        >
                            <RebelIcon className="fas fa-calendar-day" style={{ marginRight: '8px' }} />
                            Activities ({activities.length})
                        </TabButton>
                    </TabGroup>
                    <TabGroup>
                        <TabButton
                            onClick={handleCancel}
                            isBackButton
                        >
                            <RebelIcon className="fas fa-arrow-left" style={{ marginRight: '8px' }} />
                            Back
                        </TabButton>
                    </TabGroup>
                </TabContainer>

                {activeTab === 'details' && (
                    <FormContainer>
                        <SectionTitle>Location Information</SectionTitle>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <RebelLabel>Location Name *</RebelLabel>
                                    <RebelInput
                                        type="text"
                                        value={formData.location_name}
                                        onChange={(e) => handleChange('location_name', e.target.value)}
                                        placeholder="e.g., Villa Montemayor"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <RebelLabel>Country *</RebelLabel>
                                    <RebelInput
                                        type="text"
                                        value={formData.country}
                                        onChange={(e) => handleChange('country', e.target.value)}
                                        placeholder="e.g., Costa Rica"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <RebelLabel>Currency *</RebelLabel>
                                    <RebelSelect
                                        value={formData.currency}
                                        onChange={(e) => handleChange('currency', e.target.value)}
                                    >
                                        <option value="USD">USD</option>
                                        <option value="EUR">EUR</option>
                                        <option value="GBP">GBP</option>
                                        <option value="CRC">CRC</option>
                                        <option value="MXN">MXN</option>
                                        <option value="CAD">CAD</option>
                                    </RebelSelect>
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <RebelLabel>Timezone</RebelLabel>
                                    <RebelSelect
                                        value={formData.timezone}
                                        onChange={(e) => handleChange('timezone', e.target.value)}
                                    >
                                        <option value="">Select Timezone</option>
                                        <option value="America/New_York">EST</option>
                                        <option value="America/Chicago">CST</option>
                                        <option value="America/Denver">MST</option>
                                        <option value="America/Los_Angeles">PST</option>
                                        <option value="America/Costa_Rica">Costa Rica</option>
                                        <option value="America/Mexico_City">Mexico City</option>
                                        <option value="Europe/London">GMT</option>
                                        <option value="Europe/Paris">CET</option>
                                        <option value="Asia/Tokyo">JST</option>
                                    </RebelSelect>
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <RebelLabel>Description</RebelLabel>
                            <WideTextarea
                                rows="2"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                placeholder="Brief description of the location, amenities, and highlights..."
                            />
                        </FormGroup>

                        <FormGroup>
                            <RebelLabel>Location Image URL</RebelLabel>
                            <RebelInput
                                type="text"
                                value={formData.location_image_url}
                                onChange={(e) => handleChange('location_image_url', e.target.value)}
                                placeholder="https://cdn.moniker-virtual.com/agenda/location.jpg"
                            />
                            {formData.location_image_url && (
                                <ImagePreview>
                                    <img src={formData.location_image_url} alt="Location preview" />
                                </ImagePreview>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <ToggleContainer>
                                <RebelLabel>Status</RebelLabel>
                                <ToggleSwitch>
                                    <ToggleInput
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => handleChange('is_active', e.target.checked)}
                                    />
                                    <ToggleSlider />
                                </ToggleSwitch>
                                <ToggleLabel>{formData.is_active ? 'Active' : 'Inactive'}</ToggleLabel>
                            </ToggleContainer>
                        </FormGroup>

                        <ButtonContainer>
                            <RebelSecondaryButton onClick={handleCancel} disabled={saving}>
                                Cancel
                            </RebelSecondaryButton>
                            <RebelButton onClick={handleSave} disabled={saving || !formData.location_name || !formData.country}>
                                {saving ? 'Saving...' : isNewLocation ? 'Create Location' : 'Update Location'}
                            </RebelButton>
                        </ButtonContainer>
                    </FormContainer>
                )}

                {activeTab === 'activities' && (
                    <FormContainer>
                        <HeaderRow>
                            <SectionTitle>Activities for {formData.location_name}</SectionTitle>
                            <RebelButton onClick={() => openActivityModal()}>
                                <RebelIcon className="fas fa-plus" style={{ marginRight: '8px' }} />
                                Add Activity
                            </RebelButton>
                        </HeaderRow>

                        <FilterContainer>
                            <FilterRow>
                                <FilterInput
                                    type="text"
                                    placeholder="Search activities..."
                                    value={activityFilters.searchTerm}
                                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                                />
                                <FilterSelect
                                    value={activityFilters.category}
                                    onChange={(e) => handleFilterChange('category', e.target.value)}
                                >
                                    <option value="">All Categories</option>
                                    <option value="meals">Meals</option>
                                    <option value="morning-activity">Morning Activity</option>
                                    <option value="evening-activities">Evening Activities</option>
                                    <option value="business">Business</option>
                                    <option value="free-time">Free Time</option>
                                    <option value="team-building">Team Building</option>
                                    <option value="learning">Learning</option>
                                </FilterSelect>
                            </FilterRow>
                            <FilterRow>
                                <FilterGroup>
                                    <FilterLabel>Price Range ($)</FilterLabel>
                                    <FilterInputGroup>
                                        <FilterInputSmall
                                            type="number"
                                            placeholder="Min"
                                            value={activityFilters.minPrice}
                                            onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                                        />
                                        <span>-</span>
                                        <FilterInputSmall
                                            type="number"
                                            placeholder="Max"
                                            value={activityFilters.maxPrice}
                                            onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                                        />
                                    </FilterInputGroup>
                                </FilterGroup>
                                <FilterGroup>
                                    <FilterLabel>Duration (hours)</FilterLabel>
                                    <FilterInputGroup>
                                        <FilterInputSmall
                                            type="number"
                                            placeholder="Min"
                                            value={activityFilters.minDuration}
                                            onChange={(e) => handleFilterChange('minDuration', e.target.value)}
                                        />
                                        <span>-</span>
                                        <FilterInputSmall
                                            type="number"
                                            placeholder="Max"
                                            value={activityFilters.maxDuration}
                                            onChange={(e) => handleFilterChange('maxDuration', e.target.value)}
                                        />
                                    </FilterInputGroup>
                                </FilterGroup>
                            </FilterRow>
                        </FilterContainer>

                        {filteredActivities.length === 0 && activities.length > 0 ? (
                            <EmptyState>
                                <RebelIcon className="fas fa-filter" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                                <p>No activities match your filters.</p>
                            </EmptyState>
                        ) : activities.length === 0 ? (
                            <EmptyState>
                                <RebelIcon className="fas fa-calendar-day" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                                <p>No activities yet. Click "Add Activity" to create one.</p>
                            </EmptyState>
                        ) : (
                            <ActivitiesGrid>
                                {filteredActivities.map((activity) => (
                                    <ActivityCard key={activity.id}>
                                        <ActivityImageContainer>
                                            {activity.image_url ? (
                                                <ActivityImage src={activity.image_url} alt={activity.activity_name} />
                                            ) : (
                                                <ActivityImagePlaceholder>
                                                    <RebelIcon className="fas fa-image" style={{ fontSize: '32px', opacity: 0.3 }} />
                                                </ActivityImagePlaceholder>
                                            )}
                                        </ActivityImageContainer>
                                        <ActivityContent>
                                            <ActivityTitle>{activity.activity_name || 'Unnamed Activity'}</ActivityTitle>
                                            <ActivityInfo>
                                                <InfoItem>
                                                    <RebelIcon className="fas fa-tag" />
                                                    <span>{activity.category || 'N/A'} {activity.sub_category ? `/ ${activity.sub_category}` : ''}</span>
                                                </InfoItem>
                                                <InfoItem>
                                                    <RebelIcon className="fas fa-clock" />
                                                    <span>{activity.duration_hours ? `${activity.duration_hours}h` : 'N/A'}</span>
                                                </InfoItem>
                                                <InfoItem>
                                                    <RebelIcon className="fas fa-users" />
                                                    <span>{activity.min_participants || 0} - {activity.max_participants || 0}</span>
                                                </InfoItem>
                                                <InfoItem>
                                                    <RebelIcon className="fas fa-dollar-sign" />
                                                    <span>
                                                        {activity.price_per_person ? `$${activity.price_per_person}/person` : ''}
                                                        {activity.price_per_person && activity.flat_price ? ' + ' : ''}
                                                        {activity.flat_price ? `$${activity.flat_price} flat` : ''}
                                                    </span>
                                                </InfoItem>
                                            </ActivityInfo>
                                            <ActivityActions>
                                                <RebelButton onClick={() => openActivityModal(activity)} size="sm">
                                                    <RebelIcon className="fas fa-edit" style={{ marginRight: '6px' }} />
                                                    Edit
                                                </RebelButton>
                                                <RebelDangerButton onClick={() => confirmDeleteActivity(activity)} size="sm">
                                                    <RebelIcon className="fas fa-trash" style={{ marginRight: '6px' }} />
                                                    Delete
                                                </RebelDangerButton>
                                            </ActivityActions>
                                        </ActivityContent>
                                    </ActivityCard>
                                ))}
                            </ActivitiesGrid>
                        )}
                    </FormContainer>
                )}
            </RebelCard>

            {/* Activity Modal */}
            <RebelModal isOpen={showActivityModal} toggle={closeActivityModal} size="xl">
                <RebelModalHeader toggle={closeActivityModal}>
                    {editingActivity ? 'Edit Activity' : 'Add New Activity'}
                </RebelModalHeader>
                <RebelModalBody>
                    <AgendaActivityForm
                        locationId={id}
                        activity={editingActivity}
                        onSave={closeActivityModal}
                        onCancel={closeActivityModal}
                    />
                </RebelModalBody>
            </RebelModal>

            {/* Delete Activity Modal */}
            <RebelModal isOpen={deleteActivityModal} toggle={() => setDeleteActivityModal(false)}>
                <RebelModalHeader toggle={() => setDeleteActivityModal(false)}>
                    Confirm Delete
                </RebelModalHeader>
                <RebelModalBody>
                    Are you sure you want to delete this activity?
                    <br /><br />
                    <strong>{activityToDelete?.activity_name || 'This activity'}</strong>
                    <br /><br />
                    This action cannot be undone.
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelButton onClick={() => setDeleteActivityModal(false)}>
                        Cancel
                    </RebelButton>
                    <RebelDangerButton onClick={handleDeleteActivity}>
                        Delete
                    </RebelDangerButton>
                </RebelModalFooter>
            </RebelModal>
        </React.Fragment>
    );
};

export default AgendaLocationForm;

const TabContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 24px;
    border-bottom: 2px solid rgba(255, 255, 255, 0.1);
    padding: 0 24px;
`;

const TabGroup = styled.div`
    display: flex;
    gap: 8px;
`;

const TabButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props =>
        props.isBackButton
            ? 'linear-gradient(135deg, rgba(225, 112, 85, 0.3) 0%, rgba(214, 48, 49, 0.3) 100%)'
            : props.active
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'transparent'
    };
    color: ${props =>
        props.isBackButton
            ? '#ffb0a0'
            : props.active
                ? '#ffffff'
                : props.disabled
                    ? '#444'
                    : '#b0b0b0'
    };
    border: ${props =>
        props.isBackButton
            ? 'none'
            : props.active
                ? 'none'
                : '1px solid rgba(255, 255, 255, 0.15)'
    };
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.3s ease;
    border-radius: 8px 8px 0 0;
    opacity: ${props => props.disabled ? 0.5 : 1};

    &:hover {
        background: ${props =>
            props.disabled
                ? 'transparent'
                : props.isBackButton
                    ? 'linear-gradient(135deg, rgba(225, 112, 85, 0.5) 0%, rgba(214, 48, 49, 0.5) 100%)'
                    : props.active
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'rgba(255, 255, 255, 0.05)'
        };
        color: ${props => props.disabled ? '#444' : '#ffffff'};
        border-color: ${props =>
            props.isBackButton
                ? 'transparent'
                : props.active
                    ? 'transparent'
                    : 'rgba(255, 255, 255, 0.3)'
        };
    }
`;

const FormContainer = styled.div`
    padding: 24px;
`;

const SectionTitle = styled.h5`
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 24px;
    padding-bottom: 12px;
    border-bottom: 2px solid rgba(102, 126, 234, 0.3);
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    ${SectionTitle} {
        margin-bottom: 0;
        border-bottom: none;
        padding-bottom: 0;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ImagePreview = styled.div`
    margin-top: 12px;

    img {
        max-width: 100%;
        max-height: 300px;
        border-radius: 8px;
        border: 1px solid rgba(255, 255, 255, 0.1);
    }
`;

const ActivitiesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
`;

const ActivityCard = styled.div`
    background: linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(40, 40, 40, 0.95) 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
        border-color: rgba(102, 126, 234, 0.5);
    }
`;

const ActivityImageContainer = styled.div`
    width: 100%;
    height: 150px;
    background: rgba(255, 255, 255, 0.05);
`;

const ActivityImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ActivityImagePlaceholder = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.02);
`;

const ActivityContent = styled.div`
    padding: 16px;
`;

const ActivityTitle = styled.h6`
    color: #ffffff;
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 12px 0;
`;

const ActivityInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
`;

const InfoItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: #b0b0b0;
    font-size: 12px;

    i {
        width: 14px;
        color: #667eea;
        font-size: 11px;
    }
`;

const ActivityActions = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 12px;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: #888;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
    border: 2px dashed rgba(255, 255, 255, 0.1);

    p {
        margin-top: 16px;
        font-style: italic;
    }
`;

const FilterContainer = styled.div`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
`;

const FilterRow = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const FilterInput = styled.input`
    flex: 1;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #ffffff;
    padding: 10px 16px;
    border-radius: 6px;
    font-size: 14px;

    &:focus {
        outline: none;
        border-color: #667eea;
        background: rgba(255, 255, 255, 0.08);
    }

    &::placeholder {
        color: #666;
    }
`;

const FilterSelect = styled.select`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #ffffff;
    padding: 10px 16px;
    border-radius: 6px;
    font-size: 14px;
    min-width: 200px;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: #667eea;
        background: rgba(255, 255, 255, 0.08);
    }

    option {
        background: #2d2d2d;
        color: #ffffff;
    }
`;

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FilterLabel = styled.label`
    color: #b0b0b0;
    font-size: 12px;
    font-weight: 500;
`;

const FilterInputGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    span {
        color: #666;
    }
`;

const FilterInputSmall = styled.input`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #ffffff;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    width: 80px;

    &:focus {
        outline: none;
        border-color: #667eea;
        background: rgba(255, 255, 255, 0.08);
    }

    &::placeholder {
        color: #666;
    }
`;

const ToggleContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;

    /* Ensure Status label is vertically centered */
    > label {
        margin-bottom: 0;
        line-height: 1;
    }
`;

const ToggleSwitch = styled.label`
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    height: 26px;
`;

const ToggleInput = styled.input`
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + span {
        background: linear-gradient(135deg, #4a9eff 0%, #667eea 100%);
    }

    &:checked + span:before {
        transform: translateX(24px);
    }
`;

const ToggleSlider = styled.span`
    position: relative;
    display: inline-block;
    width: 50px;
    height: 26px;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 34px;
    transition: 0.3s;

    &:before {
        content: "";
        position: absolute;
        height: 20px;
        width: 20px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        border-radius: 50%;
        transition: 0.3s;
    }
`;

const ToggleLabel = styled.span`
    color: #ffffff;
    font-size: 14px;
    line-height: 1;
    display: inline-block;
`;

const WideTextarea = styled(RebelTextarea)`
    width: 100%;
    min-height: 60px;
    max-height: 120px;
    resize: vertical;
    font-size: 14px;
    line-height: 1.6;
`;

// Override reactstrap FormGroup to ensure labels are always above inputs
const FormGroup = styled.div`
    margin-bottom: 1rem;

    label {
        display: block;
        margin-bottom: 0.5rem;
    }

    /* Exception for toggle containers - labels should be inline */
    ${ToggleContainer} label {
        display: inline;
        margin-bottom: 0;
    }

    /* Custom select styling to match input height exactly */
    select {
        height: 46px;
        padding: 12px 16px;
        line-height: 1.5;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 12px center;
        background-size: 12px;
        padding-right: 36px;
    }

    /* Match input height */
    input[type="text"],
    input[type="number"],
    input[type="email"],
    input[type="password"] {
        height: 46px;
    }
`;
